import { useLayoutEffect, useRef, useState } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useAuth } from '@ohpen/react-auth';
import { createAccessTokenProvider } from './accessTokenProvider';

/** The access token for API calls, renewed first when the SSO session has outlived it. */
export const useAccessToken = (): (() => Promise<string | null>) => {
  const { getAccessToken } = useAuth();
  const oidc = useOidcAuth();

  // Read through refs so the returned function keeps one identity across renders. It is an
  // effect dependency in PmpConfigProvider and an interceptor input in useApiClient, both of
  // which redo their work on a new one, while `oidc` changes on every dispatch of its own.
  const getAccessTokenRef = useRef(getAccessToken);
  const oidcRef = useRef(oidc);

  // A layout effect, so the refs are current before the passive effects of descendants such as
  // PmpConfigProvider, which run before this component's own.
  useLayoutEffect(() => {
    getAccessTokenRef.current = getAccessToken;
    oidcRef.current = oidc;
  });

  const [provider] = useState(() =>
    createAccessTokenProvider({
      getOidcUser: () => oidcRef.current.user,
      getStoredToken: () => getAccessTokenRef.current(),
      renew: () => oidcRef.current.signinSilent(),
    }),
  );

  return provider;
};
